/**
 * TaskItem Component
 * Individual task row with status indicator and action buttons
 * Design based on Figma specifications
 */

import { X } from "lucide-react";
import type { Task } from "../types";
import { TASK_TYPE_COLORS } from "../constants";
import { cn } from "@/shared/utils/classname-merger";

interface TaskItemProps {
  task: Task;
  isLoading: boolean;
  onContinue: (task: Task) => void;
  onDismiss: (taskId: string) => void;
}

export function TaskItem({
  task,
  isLoading,
  onContinue,
  onDismiss,
}: TaskItemProps) {
  const dotColor = TASK_TYPE_COLORS[task.task_type];

  return (
    <div
      className={cn(
        "group flex flex-row items-start p-3 gap-2 bg-[#F8FAFC] rounded-lg h-11 cursor-pointer hover:bg-[#F1F5F9] transition-colors",
        isLoading && "cursor-wait opacity-70"
      )}
      onClick={() => !isLoading && onContinue(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!isLoading) onContinue(task);
        }
      }}
      aria-label={`Continue task: ${[task.vendor_name, task.product_name, task.message].filter(Boolean).join(' ')}`}
    >
      {/* Status Dot Container */}
      <div className="flex items-center justify-center w-5 h-5 p-1.5">
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: dotColor }}
          aria-hidden="true"
        />
      </div>

      {/* Task Message */}
      <div className="flex items-center flex-grow min-w-0">
        <span className="text-sm leading-5 text-[#0F172B] truncate">
          <span className="font-medium">
            {[task.vendor_name, task.product_name].filter(Boolean).join(' ')}
          </span>
          {' '}
          <span className="font-normal">
            {task.message}
          </span>
        </span>
      </div>

      {/* Dismiss Button (X) - visible on hover only */}
      <div className="flex flex-row items-start flex-shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(task.id);
          }}
          disabled={isLoading}
          className={cn(
            "flex items-center justify-center w-5 h-5",
            "opacity-0 group-hover:opacity-100 transition-opacity",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
          aria-label={`Dismiss task: ${[task.vendor_name, task.product_name, task.message].filter(Boolean).join(' ')}`}
        >
          <X
            className="w-5 h-5"
            style={{ color: "#90A1B9" }}
            strokeWidth={1.5}
          />
        </button>
      </div>
    </div>
  );
}

export default TaskItem;
